import { Country } from '@resurs-internal/mui/assets/countries';

import { type AvailableCountry } from '@/models';
import { InterestRateType } from '@/types/consumer-deposit-application-service';

export enum SectionType {
  ContactInfo = 'contactInfo',
  DisbursementAccount = 'disbursementAccount',
  Autogiro = 'autogiro',
  Bankruptcy = 'bankruptcy',
  Kyc = 'kyc',
}

export const getSectionMap = (
  country: AvailableCountry,
  productType: InterestRateType | undefined,
): SectionType[] => {
  const isFixedProduct = productType === InterestRateType.FIXED;

  const sectionMaps: Record<AvailableCountry, SectionType[]> = {
    [Country.DK]: [SectionType.ContactInfo, SectionType.Kyc],
    [Country.NO]: isFixedProduct
      ? [SectionType.ContactInfo, SectionType.Bankruptcy, SectionType.Kyc]
      : [
          SectionType.ContactInfo,
          SectionType.DisbursementAccount,
          SectionType.Bankruptcy,
          SectionType.Kyc,
        ],

    [Country.SE]: isFixedProduct
      ? [SectionType.ContactInfo, SectionType.Kyc]
      : [SectionType.ContactInfo, SectionType.Autogiro, SectionType.Kyc],
  };

  return sectionMaps[country] || [];
};

export const getSectionCount = (
  country: AvailableCountry,
  productType?: InterestRateType | undefined,
  includesFixedTermStep: boolean = false,
) => {
  const sections = getSectionMap(country, productType);

  return (
    sections.length +
    (sections.includes(SectionType.Bankruptcy) ? 1 : 2) +
    (includesFixedTermStep ? 1 : 0)
  );
};

/**
 * The last application section, which is the one to return to from the summary.
 * Sections are indexes into the section map, which only holds the application
 * sections. The progress bar has more steps than the map, such as choose
 * account, fixed term and summary, so the section count cannot be used here.
 */
export const getSectionBeforeSummary = (
  country: AvailableCountry,
  productType: InterestRateType | undefined,
): number => getSectionMap(country, productType).length - 1;

export const hasSectionType = (
  country: AvailableCountry,
  productType: InterestRateType | undefined,
  sectionType: SectionType,
): boolean => {
  return getSectionMap(country, productType).includes(sectionType);
};

export const getSectionType = (
  section: number,
  country: AvailableCountry,
  productType: InterestRateType | undefined,
): SectionType | null => {
  const sections = getSectionMap(country, productType);

  return sections[section] || null;
};
